/**
 * Every handled-error code the TypeScript app can raise.
 *
 * This list exists so the presentation registry can be *exhaustive*: it is the
 * `Record` key set that `presentation.ts` must satisfy, which turns "someone
 * added an error code and never wrote the copy" from a runtime shrug into a
 * `pnpm typecheck` failure.
 *
 * Kept honest by `__tests__/codes.unit.test.ts`, which scans `src/`, `ee/` and
 * the workspace `packages/` for every code a `HandledError` subclass declares
 * and fails when this list and the code drift apart in either direction. All
 * three trees raise handled errors, and a scan that covers only one is a list
 * that silently stops being exhaustive. Adding a code here without
 * throwing it anywhere is just as much a bug as the reverse — it means dead
 * copy, which is how `recipient_not_in_team` survived in the automations
 * explainer for a code nothing raises.
 *
 * Codes declared by the Go services are NOT here — they come from
 * `goErrorCodes` in `@langwatch/handled-error`, generated by `cmd/herrgen`.
 */
export const APP_ERROR_CODES = [
  "agent_report_rate_limited",
  "ai_call_failed",
  "ai_query_provider_error",
  "already_organization_member",
  "anomaly_rule_not_found",
  "api_key_already_revoked",
  "api_key_not_found",
  "api_key_not_owned",
  "api_key_permission_denied",
  "api_key_reserved_name",
  "api_key_scope_violation",
  "avatar_image_processing_failed",
  "avatar_image_too_large",
  "avatar_image_type_unsupported",
  "avatar_image_unreadable",
  "avatar_rate_limited",
  "billing_currency_unsupported",
  "billing_customer_deleted",
  "billing_customer_email_required",
  "billing_plan_price_missing",
  "billing_provider_unavailable",
  "broadcaster_not_active",
  "cache_rule_not_found",
  "cannot_impersonate_admin",
  "cannot_impersonate_deactivated_user",
  "clickhouse_unavailable",
  "codex_auth_failed",
  "custom_role_not_found",
  "dataset_column_type_change_unsupported",
  "dataset_name_taken",
  "dataset_not_ready",
  "dataset_stale_columns",
  "dspy_step_not_found",
  "evaluation_not_found",
  "evaluator_config_error",
  "evaluator_execution_error",
  "evaluator_input_too_large",
  "evaluator_missing_field",
  "evaluator_not_found",
  "experiment_not_found",
  "export_failed",
  "filter_field_unknown",
  "filter_parse_error",
  "gateway_budget_not_found",
  "gateway_group_budget_unsupported",
  "gateway_guardrail_project_mismatch",
  "gateway_scope_org_mismatch",
  "gateway_spend_unavailable",
  "guardrail_attach_forbidden",
  "ingestion_source_cap_reached",
  "ingestion_source_not_found",
  // Also a Go code, with copy already written under the shared/transport
  // heading — the REST boundary in `packages/api` puts it on the wire as the
  // code for an unhandled failure, so the scan sees it here too.
  "internal_error",
  "invalid_action_params",
  "invalid_email_recipient",
  "invalid_experiment_configuration",
  "langy_agent_at_capacity",
  "langy_agent_errored",
  "langy_agent_session_lost",
  "langy_agent_unavailable",
  "langy_codex_plan_limit",
  "langy_conversation_not_found",
  "langy_conversation_not_owned",
  "langy_credential_resolution",
  "langy_dispatch_rejected",
  "langy_egress_misconfigured",
  "langy_empty_message",
  "langy_github_not_connected",
  "langy_github_repo_not_accessible",
  "langy_idempotency_mismatch",
  "langy_insufficient_scope",
  "langy_model_not_allowed",
  "langy_model_not_configured",
  "langy_not_enabled",
  "langy_rate_limited",
  "langy_turn_in_progress",
  "langy_turn_not_stoppable",
  "langy_turn_timeout",
  "langy_worker_restarting",
  "langy_worker_spawn_failed",
  "langy_worker_stopped",
  "license_expired",
  "license_key_invalid",
  "license_signing_failed",
  "license_signing_key_encrypted",
  "license_signing_key_not_pem",
  "lite_member_restricted",
  "malformed_custom_role_permissions",
  "malformed_request",
  "missing_annotator",
  // Not declared by a subclass anywhere: `nlpgo/goHandledError.ts` promotes a
  // relayed Go error's `meta.reason` to the code, so this one is minted at the
  // boundary rather than thrown. It still reaches a customer, so it still needs
  // copy — see RELAYED_META_CODES in `__tests__/codes.unit.test.ts`.
  "missing_provider",
  "missing_slack_bot_token",
  "missing_slack_webhook",
  "model_not_configured",
  "model_provider_anchor_required",
  "model_provider_disabled",
  "model_provider_not_found",
  "model_provider_scope_forbidden",
  "model_provider_scopes_required",
  "no_admin_configured",
  // Also a Go code, with copy already written under the shared/transport
  // heading — `ee/admin/routes/admin.ts` raises it to hide the admin surface
  // from a session that is no longer an admin.
  "not_found",
  "notification_delivery_error",
  "organization_not_found",
  "organization_not_found_for_team",
  "project_not_found",
  "project_permission_denied",
  "prompt_not_found",
  "provider_key_invalid",
  "provider_key_missing",
  "provider_key_restricted",
  "provider_refused",
  "provider_service_disabled",
  "provider_unreachable",
  "published_workflow_version_not_found",
  "query_memory_exceeded",
  "query_timeout",
  "resource_limit_exceeded",
  "rum_ingest_disabled",
  "rum_payload_invalid",
  "rum_payload_too_large",
  "rum_rate_limited",
  "run_not_found",
  "scenario_set_limit_exceeded",
  "schema_failure",
  "seat_billing_unavailable",
  "share_link_exhausted",
  "share_link_expired",
  "share_link_forbidden",
  "share_link_not_found",
  "share_read_rate_limited",
  "span_not_found",
  "spend_source_unavailable",
  "subscription_service_unavailable",
  "subscription_sync_failed",
  "suite_all_scenarios_archived",
  "suite_all_targets_archived",
  "suite_invalid_scenario_references",
  "suite_invalid_target_references",
  "suite_name_taken",
  "suite_not_found",
  "system_prompt_conflict",
  "system_prompt_required",
  "team_membership_not_found",
  "team_not_found",
  "template_immutable",
  "template_not_found",
  "template_validation_error",
  "test_fire_unavailable",
  "time_range_too_wide",
  "trace_not_evaluatable",
  "trace_not_found",
  "trace_sharing_disabled",
  // Also a Go code, with copy already written under the shared/transport
  // heading — an app-level subclass raises it too (`ee/admin/routes/admin.ts`),
  // and the scan is right to insist it be listed here as well.
  "unauthorized",
  "usage_report_failed",
  "user_to_impersonate_not_found",
  // Raised by the shared package rather than an app-level subclass:
  // `ValidationError` (and `ValidationError.fromZodError`, which the tRPC
  // boundary uses to promote input ZodErrors onto the handled channel).
  "validation_error",
  "virtual_key_not_found",
  "workflow_execution_failed",
  "workflow_not_found",
] as const;

export type AppErrorCode = (typeof APP_ERROR_CODES)[number];
